<template>
  <div>
    <!-- Обрыв связи: рабочий экран закрыт, показан один информационный экран. -->
    <div v-if="blocked" class="connection-blocked">
      <div class="connection-blocked__card">
        <u-icon class="connection-blocked__icon" :icon="icon" />

        <h2 class="connection-blocked__title">{{ currentBanner.title }}</h2>
        <p class="connection-blocked__text">{{ currentBanner.text }}</p>

        <div v-if="schoolName" class="connection-blocked__row">
          <span class="connection-blocked__label">Школа</span>
          <span class="connection-blocked__value">{{ schoolName }}</span>
        </div>

        <div v-if="lastOkAt" class="connection-blocked__row">
          <span class="connection-blocked__label">Последние данные</span>
          <span class="connection-blocked__value">{{
            formatTime(lastOkAt)
          }}</span>
        </div>

        <div class="connection-blocked__actions">
          <u-button variant="primary" @click="retry">Проверить сейчас</u-button>

          <span class="connection-blocked__hint">
            <u-icon
              v-if="probing"
              class="connection-blocked__spinner"
              icon="mdi-loading mdi-spin"
            />
            Проверяем связь автоматически
          </span>
        </div>
      </div>
    </div>

    <transition name="connection-banner">
      <div v-if="showRecovered" class="connection-banner">
        <u-icon class="connection-banner__icon" :icon="recoveredIcon" />

        <div>
          <p class="connection-banner__title">Связь восстановлена</p>
          <p class="connection-banner__text">Данные обновляются.</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import Api from "@/app/services/Api";
import { isExternalSite } from "@/app/services/environment";

/**
 * Пауза перед пробой связи. Пробу отвечает облако и школу не опрашивает, поэтому
 * держим её короткой: пинг в облаке обновляется раз в 10 секунд — чаще проверять
 * смысла нет, но и затягивать возврат к работе нельзя.
 */
const PROBE_DELAYS = [5000, 10000];
const PROBE_JITTER = 0.15;
const RECOVERED_TIMEOUT = 4000;

const ICONS = {
  warning: "mdi-alert-outline",
  danger: "mdi-alert-outline",
  success: "mdi-check-circle-outline"
};

function formatTime(timestamp) {
  return new Date(timestamp).toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit"
  });
}

export default {
  name: "ConnectionBanner",

  data() {
    return {
      probeTimer: null,
      probeAttempt: 0,
      recoveredVisible: false,
      recoveredTimer: null
    };
  },

  computed: {
    ...mapState("connection", [
      "status",
      "lastOkAt",
      "schoolName",
      "probing",
      "recoveredAt"
    ]),

    ...mapGetters("connection", ["isDown", "bannerInfo"]),

    isLoginPage() {
      return this.$route.name === "login";
    },

    /**
     * Пока связи нет, работать нельзя — экран закрывается информационным
     * экраном. Но только на внешней площадке (`canProbe`): там есть отдельное
     * облако, состояние которого можно перепроверить, и подмена данных
     * опасна. Внутри школы (прямая работа с little) причина сбоя видна инлайн
     * во вьюхах, и накрывать весь интерфейс, пряча кнопку повтора, незачем.
     */
    blocked() {
      return this.isDown && this.canProbe;
    },

    showRecovered() {
      return !this.isDown && this.recoveredVisible;
    },

    /**
     * Проба опрашивает облако (/teacher/school), поэтому нужна только на внешней
     * площадке: внутри школы и интерфейс, и данные живут на одном сервере.
     */
    canProbe() {
      return isExternalSite() && !this.isLoginPage;
    },

    currentBanner() {
      return this.bannerInfo || {};
    },

    icon() {
      return ICONS[this.currentBanner.variant] || ICONS.danger;
    },

    recoveredIcon() {
      return ICONS.success;
    }
  },

  watch: {
    isDown(value) {
      if (value && this.canProbe) this.scheduleProbe();
      else this.stopProbe();
    },

    recoveredAt(at) {
      if (!at) return;

      this.recoveredVisible = true;
      clearTimeout(this.recoveredTimer);
      this.recoveredTimer = setTimeout(() => {
        this.recoveredVisible = false;
      }, RECOVERED_TIMEOUT);
    }
  },

  mounted() {
    if (this.isDown && this.canProbe) this.scheduleProbe();

    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
  },

  beforeDestroy() {
    this.stopProbe();
    clearTimeout(this.recoveredTimer);
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
  },

  methods: {
    formatTime,

    /**
     * Сеть устройства вернулась. На внешней площадке сразу проверяем облако,
     * внутри школы — просим вьюхи перечитать данные: факт сети не значит, что
     * школьный сервер снова отвечает.
     */
    handleOnline() {
      if (!this.isDown) return;

      if (this.canProbe) {
        this.probeAttempt = 0;
        this.probe();
      } else {
        this.$store.dispatch("connection/bumpReload");
      }
    },

    handleOffline() {
      this.stopProbe();
      this.$store.dispatch("connection/failure", {
        kind: "offline",
        reason: "offline"
      });
    },

    scheduleProbe() {
      this.stopProbe();

      const delay =
        PROBE_DELAYS[Math.min(this.probeAttempt, PROBE_DELAYS.length - 1)];
      const jitter = delay * PROBE_JITTER * (Math.random() * 2 - 1);

      this.probeAttempt = Math.min(this.probeAttempt + 1, PROBE_DELAYS.length);

      this.probeTimer = setTimeout(
        () => this.probe(),
        Math.max(1000, Math.round(delay + jitter))
      );
    },

    stopProbe() {
      if (!this.probeTimer) return;

      clearTimeout(this.probeTimer);
      this.probeTimer = null;
    },

    /** Проба состояния школы — отвечает облако, школа не опрашивается. */
    async probe() {
      this.probeTimer = null;

      if (this.probing || !this.canProbe) {
        if (this.canProbe) this.scheduleProbe();
        return;
      }

      await this.$store.dispatch("connection/probeStarted");

      try {
        const { data } = await Api.connection.probe();

        if (data && data.name) {
          await this.$store.dispatch("connection/schoolKnown", data.name);
        }

        if (data && data.state === "online") {
          this.probeAttempt = 0;
          await this.$store.dispatch("connection/success");
        } else {
          await this.$store.dispatch("connection/failure", {
            kind: "school_down",
            reason: "no_ping"
          });
          this.scheduleProbe();
        }
      } catch (error) {
        const info = Api.describeError(error);

        if (info.kind === "session") {
          // Проба идёт с флагом __silent, поэтому интерсептор не уводит на вход
          // сам. Делаем это явно: иначе оверлей останется на экране навсегда,
          // а кнопка «Проверить сейчас» будет повторять тот же 401.
          this.stopProbe();
          this.$router.push({ name: "login" }).catch(() => {});
          return;
        }

        await this.$store.dispatch("connection/failure", {
          kind: info.kind === "offline" ? "offline" : "cloud_down",
          reason: info.reason
        });
        this.scheduleProbe();
      } finally {
        await this.$store.dispatch("connection/probeFinished");
      }
    },

    retry() {
      this.probeAttempt = 0;

      if (this.canProbe) this.probe();
      else this.$store.dispatch("connection/bumpReload");
    }
  }
};
</script>

<style scoped>
.connection-blocked {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background-color: rgba(255, 255, 255, 0.97);
}

.connection-blocked__card {
  width: 100%;
  max-width: 30rem;
  padding: 2rem;
  border-radius: 0.75rem;
  border-left: 0.25rem solid #d97706;
  background-color: #ffffff;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  text-align: center;
}

.connection-blocked__icon {
  font-size: 3rem;
  color: #d97706;
}

.connection-blocked__title {
  margin-top: 1rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
}

.connection-blocked__text {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #4b5563;
}

.connection-blocked__row {
  display: flex;
  justify-content: space-between;
  margin-top: 0.75rem;
  font-size: 0.875rem;
}

.connection-blocked__label {
  color: #6b7280;
}

.connection-blocked__value {
  font-weight: 500;
  color: #1f2937;
  text-align: right;
}

.connection-blocked__actions {
  margin-top: 1.5rem;
}

.connection-blocked__hint {
  display: block;
  margin-top: 0.75rem;
  font-size: 0.75rem;
  color: #9ca3af;
}

.connection-blocked__spinner {
  margin-right: 0.25rem;
}

.connection-banner {
  position: fixed;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 300;
  display: flex;
  align-items: center;
  width: calc(100% - 2rem);
  max-width: 32rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background-color: #16a34a;
  color: #ffffff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
}

.connection-banner__icon {
  flex-shrink: 0;
  margin-right: 0.75rem;
  font-size: 1.5rem;
}

.connection-banner__title {
  font-weight: 600;
}

.connection-banner__text {
  font-size: 0.75rem;
  opacity: 0.9;
}

.connection-banner-enter-active,
.connection-banner-leave-active {
  transition: opacity 0.2s ease;
}

.connection-banner-enter,
.connection-banner-leave-to {
  opacity: 0;
}
</style>
